/**
 * ScheduledMessagesTable
 * -----------------------------------------------------------------------------
 * Tabela reutilizável de mensagens agendadas. Extraída de ScheduledMessagesPage
 * para que possa ser usada também pelo modal por-conversa
 * (ConversationScheduledMessagesModal) no header do chat.
 *
 * Mantém EXATAMENTE o mesmo layout visual da página global, apenas parametrizando
 * os callbacks de ação e adicionando um pequeno badge "Macro" quando houver
 * macro vinculada ao agendamento.
 *
 * @version 1.1.0
 * RELEASE: 2026-07-09 v1.1.0 - Exibe o botão "Cancelar" também para agendamentos
 *                              com status='failed', permitindo dispensar itens
 *                              com falha permanente. O registro fica com status
 *                              "Cancelada" e some do contador da tarja vermelha
 *                              no header do chat. Enviar agora/Editar continuam
 *                              restritos a 'pending'.
 * RELEASE: 2026-06-02 v1.0.0 - Extração para reuso entre página global e modal
 *                              por-conversa do header do chat.
 */
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Loader2,
  Pencil,
  Send,
  XCircle,
  MessageCircle,
  MessageSquareReply,
  Instagram,
  Mail,
  Globe,
  MessageSquare,
  Zap,
  CheckCheck,
  Check,
  Clock,
  X,
  Minus,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { cn } from '@/lib/utils';


/** Item retornado pela RPC list_scheduled_chat_messages (subset usado aqui). */
export interface ScheduledMessageRow {
  id: string;
  conversation_id: string;
  inbox_name: string;
  inbox_channel_type: string | null;
  contact_name: string;
  contact_phone: string;
  remind_at: string;
  status: string;
  attempts: number;
  cancel_on_reply: boolean;
  internal_note: string | null;
  error_details: string | null;
  message_payload: any;
  created_by_name: string | null;
  /** v1.1.0 — avatar do usuário criador (para SmartAvatar). */
  created_by_avatar?: string | null;
  /** v1.1.0 — data de criação do agendamento. */
  created_at?: string | null;
  /** v1.1.0 — status de leitura da mensagem enviada (pending/sent/delivered/read/failed). */
  message_read_status?: string | null;
  // v1.1.0: macro vinculada (opcional)
  linked_macro_rule_id?: string | null;
  linked_macro_execution_mode?: string | null;
  linked_macro_status?: string | null;
  linked_macro_error?: string | null;
}


export interface ScheduledMessagesTableProps {
  items: ScheduledMessageRow[];
  actionLoadingId?: string | null;
  canRowEdit: boolean;
  canRowDelete: boolean;
  /** Quando não fornecido, oculta a coluna "Inbox". */
  showInboxColumn?: boolean;
  /** Quando não fornecido, oculta o ícone de abrir conversa. */
  onOpenConversation?: (conversationId: string) => void;
  onEdit: (item: ScheduledMessageRow) => void;
  onSendNow: (item: ScheduledMessageRow) => void;
  onCancel: (item: ScheduledMessageRow) => void;
  /** v1.2.0 — Quando definido, a linha inteira fica clicável (abre detalhes/edição). */
  onRowClick?: (item: ScheduledMessageRow) => void;
}

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  processing: 'Processando',
  sent: 'Enviada',
  failed: 'Falhou',
  cancelled: 'Cancelada',
  skipped_replied: 'Cancelada (resposta)',
};

const STATUS_DOT_COLOR: Record<string, string> = {
  pending: 'bg-amber-500',
  processing: 'bg-blue-500',
  sent: 'bg-emerald-500',
  failed: 'bg-destructive',
  cancelled: 'bg-muted-foreground',
  skipped_replied: 'bg-muted-foreground',
};

const MACRO_MODE_LABEL: Record<string, string> = {
  before_message: 'antes',
  after_message: 'depois',
  replace_message: 'substitui',
};

/** Ícone + cor para o canal da inbox (igual ao usado em outras telas). */
function getChannelIconMeta(channelType?: string | null) {
  const t = (channelType || '').toLowerCase();
  if (t.includes('instagram')) return { Icon: Instagram, color: 'text-pink-500' };
  if (t.includes('mail') || t.includes('email'))
    return { Icon: Mail, color: 'text-blue-500' };
  if (t.includes('web') || t.includes('chat'))
    return { Icon: Globe, color: 'text-cyan-500' };
  if (t.includes('whatsapp'))
    return { Icon: MessageSquare, color: 'text-green-500' };
  return { Icon: MessageSquare, color: 'text-muted-foreground' };
}

/**
 * Renderiza status de leitura da mensagem enviada (mesmo padrão da régua de
 * cobrança em ReminderQueueDialog). Recebe o valor cru de `messages.status`.
 */
function renderReadStatus(status?: string | null) {
  if (!status) {
    return <span className="text-muted-foreground">—</span>;
  }
  const config: Record<
    string,
    { label: string; Icon: React.ElementType; colorClass: string }
  > = {
    read: { label: 'Lida', Icon: CheckCheck, colorClass: 'text-blue-600' },
    delivered: { label: 'Entregue', Icon: CheckCheck, colorClass: 'text-muted-foreground' },
    sent: { label: 'Enviada', Icon: Check, colorClass: 'text-muted-foreground' },
    pending: { label: 'Pendente', Icon: Clock, colorClass: 'text-amber-600' },
    failed: { label: 'Falhou', Icon: X, colorClass: 'text-destructive' },
  };
  const c = config[status] || { label: status, Icon: Minus, colorClass: 'text-muted-foreground' };
  const Icon = c.Icon;
  return (
    <div className="flex items-center gap-1 text-xs font-medium">
      <Icon className={cn('h-3.5 w-3.5', c.colorClass)} />
      <span>{c.label}</span>
    </div>
  );
}


export function ScheduledMessagesTable({
  items,
  actionLoadingId,
  canRowEdit,
  canRowDelete,
  showInboxColumn = true,
  onOpenConversation,
  onEdit,
  onSendNow,
  onCancel,
  onRowClick,
}: ScheduledMessagesTableProps) {
  return (
    <div className="border rounded-md overflow-hidden">
      <Table
        scrollViewport
        className="min-w-[1280px]"
      >
        <TableHeader>
          <TableRow>
            <TableHead>Quando</TableHead>
            <TableHead className="hidden md:table-cell">Contato</TableHead>
            {showInboxColumn && <TableHead className="hidden md:table-cell">Inbox</TableHead>}
            <TableHead>Mensagem</TableHead>
            {/* v1.2.0 — coluna Template (somente exibe valor para WhatsApp Oficial) */}
            <TableHead className="hidden md:table-cell">Template</TableHead>
            <TableHead className="hidden md:table-cell">Status</TableHead>
            <TableHead className="hidden md:table-cell">Visualizado</TableHead>
            <TableHead className="whitespace-nowrap hidden md:table-cell">Criado em</TableHead>
            <TableHead className="hidden md:table-cell">Criado por</TableHead>
            <TableHead className="text-right hidden md:table-cell">Tent.</TableHead>
            <TableHead className="hidden md:table-cell">Erro</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>

        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const isPending = item.status === 'pending';
            // v1.1.0: também permite cancelar itens em 'failed' para que o
            // usuário possa dispensar mensagens com falha permanente e removê-las
            // do contador da tarja vermelha no header do chat.
            const isFailed = item.status === 'failed';
            const canCancel = isPending || isFailed;
            const text =
              item.message_payload?.text ||
              item.message_payload?.caption ||
              '(sem texto)';
            const hasMacro = !!item.linked_macro_rule_id;
            const macroFailed = item.linked_macro_status === 'failed';

            return (
              <TableRow
                key={item.id}
                className={onRowClick ? 'cursor-pointer hover:bg-muted/40' : undefined}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
              >
                <TableCell className="whitespace-nowrap text-xs">
                  <div className="font-medium text-foreground">
                    {format(
                      new Date(item.remind_at),
                      "dd/MM/yyyy 'às' HH:mm",
                      { locale: ptBR },
                    )}
                  </div>
                  {item.cancel_on_reply && (
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-0.5">
                      <MessageSquareReply className="h-3 w-3" />
                      Cancela se responder
                    </div>
                  )}
                </TableCell>

                <TableCell className="text-sm hidden md:table-cell">
                  <div className="font-medium">{item.contact_name || '—'}</div>
                  <div className="text-xs text-muted-foreground">
                    {item.contact_phone}
                  </div>
                </TableCell>

                {showInboxColumn && (
                  <TableCell className="text-xs text-muted-foreground hidden md:table-cell">
                    {(() => {
                      const { Icon, color } = getChannelIconMeta(
                        item.inbox_channel_type,
                      );
                      return (
                        <div className="flex items-center gap-1.5">
                          <Icon className={`h-3.5 w-3.5 shrink-0 ${color}`} />
                          <span className="truncate">{item.inbox_name}</span>
                        </div>
                      );
                    })()}
                  </TableCell>
                )}

                <TableCell className="min-w-[160px] md:max-w-[280px]">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="text-sm truncate">{text}</p>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="max-w-sm whitespace-pre-wrap"
                    >
                      {text}
                    </TooltipContent>
                  </Tooltip>
                  {item.internal_note && (
                    <p className="text-[10px] italic text-muted-foreground truncate mt-0.5">
                      📝 {item.internal_note}
                    </p>
                  )}
                  {hasMacro && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={`mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${
                            macroFailed
                              ? 'bg-destructive/10 text-destructive'
                              : 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
                          }`}
                        >
                          <Zap className="h-2.5 w-2.5" />
                          Macro{' '}
                          {MACRO_MODE_LABEL[
                            item.linked_macro_execution_mode ?? 'after_message'
                          ] ?? ''}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm">
                        {macroFailed
                          ? `Falha ao executar macro: ${
                              item.linked_macro_error ?? 'erro desconhecido'
                            }`
                          : 'Ações rápidas vinculadas a esta mensagem.'}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </TableCell>

                {/* v1.2.0 — Template (somente WhatsApp Oficial). Mostra nome do
                    template no modo `template` ou do fallback no modo
                    `text_with_fallback`; nos demais casos exibe "—". */}
                <TableCell className="hidden md:table-cell text-xs">
                  {(() => {
                    const isWaba = (item.inbox_channel_type || '').toLowerCase() === 'whatsapp_oficial';
                    if (!isWaba) return <span className="text-muted-foreground">—</span>;
                    const tpl = item.message_payload?.template;
                    const fb = item.message_payload?.fallback_template;
                    const chosen = tpl ?? fb;
                    if (!chosen?.name) return <span className="text-muted-foreground">—</span>;
                    const params = Array.isArray(chosen.body_params) ? chosen.body_params.length : 0;
                    return (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="inline-flex items-center gap-1 max-w-[180px]">
                            <span className="truncate font-medium">{chosen.name}</span>
                            {fb && !tpl && (
                              <span className="text-[9px] px-1 py-0.5 rounded bg-muted text-muted-foreground shrink-0">
                                fallback
                              </span>
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          {chosen.name} · {chosen.language || '—'} · {params} var.
                        </TooltipContent>
                      </Tooltip>
                    );
                  })()}
                </TableCell>

                <TableCell className="hidden md:table-cell">
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-2 w-2 rounded-full shrink-0 ${
                        STATUS_DOT_COLOR[item.status] ?? 'bg-muted-foreground'
                      }`}
                    />
                    <span className="text-xs text-foreground">
                      {STATUS_LABEL[item.status] ?? item.status}
                    </span>
                  </div>
                </TableCell>

                {/* Visualizado — status de leitura da mensagem enviada */}
                <TableCell className="whitespace-nowrap hidden md:table-cell">
                  {renderReadStatus(item.message_read_status)}
                </TableCell>

                {/* Criado em — data/hora de criação do agendamento */}
                <TableCell className="whitespace-nowrap text-xs text-muted-foreground hidden md:table-cell">
                  {item.created_at
                    ? format(new Date(item.created_at), 'dd/MM/yy HH:mm', { locale: ptBR })
                    : '—'}
                </TableCell>

                {/* Criado por — avatar + nome */}
                <TableCell className="text-xs text-muted-foreground hidden md:table-cell">
                  <div className="flex items-center gap-2 min-w-0">
                    <SmartAvatar
                      size="xs"
                      src={item.created_by_avatar ?? undefined}
                      name={item.created_by_name ?? undefined}
                    />
                    <span className="truncate">{item.created_by_name || '—'}</span>
                  </div>
                </TableCell>

                {/* Tentativas */}
                <TableCell className="text-right text-xs tabular-nums hidden md:table-cell">
                  {item.attempts ?? 0}
                </TableCell>

                {/* Erro */}
                <TableCell className="max-w-[200px] hidden md:table-cell">
                  {item.error_details ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="text-[11px] text-destructive truncate">
                          {item.error_details}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm whitespace-pre-wrap">
                        {item.error_details}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>


                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    {onOpenConversation && item.conversation_id && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() =>
                              onOpenConversation(item.conversation_id)
                            }
                          >
                            <MessageCircle className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Abrir conversa</TooltipContent>
                      </Tooltip>
                    )}

                    {isPending && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => onSendNow(item)}
                            disabled={actionLoadingId === item.id}
                          >
                            {actionLoadingId === item.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Send className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Enviar agora</TooltipContent>
                      </Tooltip>
                    )}

                    {isPending && canRowEdit && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => onEdit(item)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Editar</TooltipContent>
                      </Tooltip>
                    )}

                    {canCancel && canRowDelete && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => onCancel(item)}
                          >
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Cancelar</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
